import { Eye, EyeOff } from "lucide-react";
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// import { updatePassword } from "@/app/actions/auth";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorPopup from "../../components/ErrorPopup";
import { useApiCall } from "../../hooks/useApiCall";
import InfiniteScrollGallery from "../Login/components/InfiniteScrollGallery";
import { authService } from "../../services/auth.service";
import { useAuth } from "../../contexts/AuthContext";

const UpdatePassword: React.FC = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { signOut } = useAuth();
    const isForced = searchParams.get('force') === 'true';

    const { isLoading, error: apiError, execute, clearError } = useApiCall({
        onSuccess: async (result) => {
          const updateResult = result as { status: string; redirectTo?: string };
          if (updateResult.status === "success") {
            const to = updateResult.redirectTo || '/dashboard';
            navigate(to, { replace: true });
          } else {
            setError(updateResult.status || 'An unexpected error occurred');
          }


          // if (updateResult.status === "success") {
          //   // Redirect to home page after a brief delay to show success message
          //   setTimeout(() => {
          //     window.location.href = '/';
          //   }, 1500);
          // } else {
          //   setError(updateResult.status || 'An unexpected error occurred');
          // }
        },
        onError: (err) => {
          console.error('Error submitting form:', err);
          const errorMessage = err instanceof Error ? err.message : String(err);
          setError(errorMessage || 'An unexpected error occurred. Please try again.');
        }
      });

      const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        
        const formData = new FormData(event.currentTarget);
        const password = formData.get('password') as string;
        const confirmPassword = formData.get('confirmPassword') as string;

        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }

        await execute(async () => {
          const result = await authService.updatePassword(password);

          if (result.error) {
            throw new Error(result.error.message);
          }
          const redirectTo = result.data?.url || '/dashboard';
          return { status: 'success', redirectTo };
        });
      }
    
      const handleCloseError = () => {
        setError(null);
        clearError();
      };

      const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
      };

      const handleConfirmLogout = async () => {
        setShowLogoutConfirm(false);
        try {
          await signOut();
          navigate('/login', { replace: true });
        } catch (error) {
          console.error('Error logging out:', error);
          // Even if logout fails, navigate to login
          navigate('/login', { replace: true });
        }
      };

      const handleCancelLogout = () => {
        setShowLogoutConfirm(false);
      };


  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <LoadingSpinner size="lg" text="Updating Password..." />
        </div>
      )}
      {isForced && (
        <button
          onClick={handleLogoutClick}
          className="fixed top-4 right-4 z-50 text-base text-[#D1293D] underline bg-[#FFEDEF] px-4 py-2 rounded-md shadow-sm"
        >
          Cancel / Logout
        </button>
      )}
      <div className="min-h-screen bg-white flex flex-col lg:flex-row w-full">
        <div className="hidden md:flex md:w-1/2 lg:w-2/3 bg-gray-100">
            <InfiniteScrollGallery />
        </div>
        <div className="w-full md:w-1/2 lg:w-1/3 flex justify-center items-center bg-white relative p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col items-center gap-4 sm:gap-6 w-full max-w-sm sm:max-w-md">
            <div className="font-poppins text-lg sm:text-xl lg:text-2xl font-semibold text-[#1a1a1a] text-center">
                Create a New Password
            </div>
            
            {/* Update Password Form */}
            <div className="w-full flex flex-col items-center justify-center">
              <form ref={formRef} onSubmit={handleSubmit} className="w-full max-w-sm sm:max-w-md space-y-4 sm:space-y-6">
                <div className="space-y-3 sm:space-y-4 w-full">

                  {apiError && (
                    <div className={`p-3 text-xs sm:text-sm rounded-lg ${
                      apiError.includes("Successfully") || apiError.includes("sent") 
                        ? "bg-green-50 border border-green-200 text-green-600"
                        : "bg-red-50 border border-red-200 text-red-500"
                    }`}>
                      {apiError}
                    </div>
                  )}
                  <div className="space-y-1.5 sm:space-y-2">
                        <label className="block text-sm sm:text-base font-medium text-gray-700">New Password</label>
                        <div className="relative">
                          <input 
                            type={showPassword ? "text" : "password"} 
                            name="password"
                            placeholder="Enter new password" 
                            className="w-full h-12 rounded-lg border border-[#d9d9d9] bg-[#ffffff] pl-3 pr-10 py-2 sm:py-3 text-base text-[#0f1d2a] placeholder:text-[#0f1d2a]/50 focus:outline-none focus:ring-2 focus:ring-[#2e4895] transition-all duration-200"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors p-1"
                          >
                            {showPassword ? <EyeOff size={16} className="sm:w-[18px] sm:h-[18px]" /> : <Eye size={16} className="sm:w-[18px] sm:h-[18px]" />}
                          </button>
                        </div>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                        <label className="block text-sm sm:text-base font-medium text-gray-700">Confirm Password</label>
                        <div className="relative">
                          <input 
                            type={showConfirmPassword ? "text" : "password"} 
                            name="confirmPassword"
                            placeholder="Confirm password" 
                            className="w-full h-12 rounded-lg border border-[#d9d9d9] bg-[#ffffff] pl-3 pr-10 py-2 sm:py-3 text-base text-[#0f1d2a] placeholder:text-[#0f1d2a]/50 focus:outline-none focus:ring-2 focus:ring-[#2e4895] transition-all duration-200"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors p-1"
                          >
                            {showConfirmPassword ? <EyeOff size={16} className="sm:w-[18px] sm:h-[18px]" /> : <Eye size={16} className="sm:w-[18px] sm:h-[18px]" />}
                          </button>
                        </div>
                  </div>
                </div>

                {/* <button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-[#0f1d2a] py-3 sm:py-3.5 rounded-lg text-[#ffffff] transition-colors duration-200 text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 h-12 font-medium"
                >
                  {isLoading ? (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                      <LoadingSpinner size="lg" text="Updating Password" />
                    </div>
                  ) : (
                    'Submit'
                  )}
                </button> */}
                <button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-[#0f1d2a] py-3 sm:py-3.5 rounded-lg text-[#ffffff] transition-colors duration-200 text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 h-12 font-medium"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner variant="inline" size="sm" />
                      <span className="hidden sm:inline">Updating Password</span>
                      <span className="sm:hidden">Updating...</span>
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ErrorPopup
        isOpen={!!error}
        onClose={handleCloseError}
        title="Reset Password Error"
        message={error || ''}
        type="error"
        actions={[
          {
            label: 'OK',
            onClick: handleCloseError,
            variant: 'primary'
          }
        ]}
      />
      <ErrorPopup
        isOpen={showLogoutConfirm}
        onClose={handleCancelLogout}
        title="Logout Confirmation"
        message="You will be redirected to the login screen. If you don't have a password, please use the 'Forgot Password' option on the login page to reset your password."
        type="info"
        actions={[
          {
            label: 'Cancel',
            onClick: handleCancelLogout,
            variant: 'secondary'
          },
          {
            label: 'Continue to Login',
            onClick: handleConfirmLogout,
            variant: 'primary'
          }
        ]}
      />
    </>
  );
};

export default UpdatePassword;
